import { useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { IndexDef, LevelBasis, LevelCode } from '@/api/types/readiness'
import { levelNum } from '@/api/types/readiness'
import { allObjectives } from '@/lib/indexes/registry'
import { computeIndexScore, computeScore, liveLevel, liveScore, reportedLevel, reportedScore } from '@/lib/readiness/score'
import { assertScale, fmtScore } from '@/lib/readiness/scale'
import { reviewGates, discoverySummaryOf, suggestedAssessmentName, type ScopeData } from '@/stores/readinessStore'
import { RdModal, RdField } from '@/components/readiness/RdModal'
import { Chip, Icon } from '@/components/readiness/primitives'
import { formatDate } from '@/components/common/format'

// Review & save assessment (I2 §8): a centred 820px modal with the fields,
// the needs-attention table (non-blocking), the blocking checks (disable Save),
// movement since the last assessment, what is recorded, and the read-only
// notice. No assessor field: the record carries created_by / created_at only.

export function ReviewSaveModal({ index, scope, actor, onClose, onSave, onAttachReport }: { index: IndexDef; scope: ScopeData; actor: string; onClose: () => void; onSave: (input: { name: string; note: string | null; as_of: string }) => void; onAttachReport?: () => void }) {
  const scale = assertScale(index)
  const today = new Date()
  // Default name never duplicates (I5 §12.1); a typed duplicate is flagged, not blocked.
  const [name, setName] = useState(suggestedAssessmentName(today, scope.snapshots.map((x) => x.name)))
  const duplicate = scope.snapshots.some((x) => x.name.trim().toLowerCase() === name.trim().toLowerCase())
  const [asOf, setAsOf] = useState(today.toISOString().slice(0, 10))
  const [note, setNote] = useState('')
  const last = scope.snapshots[scope.snapshots.length - 1] ?? null
  const minAsOf = last ? last.created_at.slice(0, 10) : undefined
  const gates = useMemo(() => reviewGates(index, scope), [index, scope])
  const items = allObjectives(index)
  const q = `?index=${index.id}`

  const movement = useMemo(() => {
    const up: Array<{ code: string; from: string; to: string }> = []
    const down: Array<{ code: string; from: string; to: string }> = []
    let unchanged = 0
    const notSet: string[] = []
    items.forEach(({ objective }) => {
      const st = scope.states[objective.code]
      if (!st || st.status === 'unset') {
        notSet.push(objective.code)
        return
      }
      const prev = last?.items.find((i) => i.objective_code === objective.code)
      if (scale === 'levels') {
        const was = prev?.level ?? 'L0'
        const now = reportedLevel(st)
        if (levelNum(now) > levelNum(was)) up.push({ code: objective.code, from: was, to: now })
        else if (levelNum(now) < levelNum(was)) down.push({ code: objective.code, from: was, to: now })
        else unchanged++
      } else {
        const was = prev?.score ?? null
        const now = reportedScore(st)
        if (was == null && now != null) up.push({ code: objective.code, from: '—', to: fmtScore(now) })
        else if ((now ?? 0) > (was ?? 0)) up.push({ code: objective.code, from: fmtScore(was), to: fmtScore(now) })
        else if ((now ?? 0) < (was ?? 0)) down.push({ code: objective.code, from: fmtScore(was), to: fmtScore(now) })
        else unchanged++
      }
    })
    // Coverage-driven drops (I3 §4.1) are shown apart: the latest downward
    // event since the last assessment carries `cause: coverage`.
    const since = last?.created_at ?? ''
    const isCoverage = (code: string) => [...(scope.events ?? [])].filter((e) => e.objective_code === code && e.occurred_at > since && e.from_level && e.to_level && levelNum(e.to_level) < levelNum(e.from_level)).pop()?.cause === 'coverage'
    const coverage = down.filter((d) => isCoverage(d.code))
    return { up, down: down.filter((d) => !isCoverage(d.code)), coverage, unchanged, notSet }
  }, [items, scope.states, scope.events, last, scale])

  const recordedScore = scale === 'levels' ? computeScore(items.map(({ objective }) => reportedLevel(scope.states[objective.code]))) : (computeIndexScore(index, (c) => reportedScore(scope.states[c])).overall ?? 0)
  const liveScoreValue = scale === 'levels' ? computeScore(items.map(({ objective }) => liveLevel(scope.states[objective.code]))) : (computeIndexScore(index, (c) => liveScore(scope.states[c])).overall ?? 0)
  const evidenceFrozen = scope.evidence.filter((e) => !e.archived_at).length
  const lastSync = scope.sync_runs.find((r) => r.status === 'succeeded')
  const summary = discoverySummaryOf(scope.settings.discovery_facts, lastSync?.finished_at ?? null)
  const bases = new Set<LevelBasis>(items.map(({ objective }) => scope.states[objective.code]?.basis ?? 'internal'))
  const basisLabel = bases.has('csa_report') ? (bases.size === 1 ? 'CSA QRI report attached' : 'Mixed · CSA QRI report attached for some') : 'Internal judgement'
  const canSave = gates.blocking.length === 0 && name.trim().length > 0
  const fmt = (v: number) => (scale === 'levels' ? String(v) : v.toFixed(1))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!canSave) return
    onSave({ name: name.trim(), note: note.trim() || null, as_of: new Date(asOf).toISOString() })
  }

  return (
    <RdModal
      title="Review & save assessment"
      width={820}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rd-btn mr-auto" onClick={onClose}>Back</button>
          <span className="self-center text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{gates.needs_attention.length} {gates.needs_attention.length === 1 ? 'level' : 'levels'} will be recorded at {gates.needs_attention.length === 1 ? 'its' : 'their'} last confirmed value</span>
          <button type="submit" form="rd-review-save" className="rd-btn rd-btn-primary" disabled={!canSave} data-testid="save-assessment-confirm">
            <Icon name="save" size={14} /> Save assessment
          </button>
        </>
      }
    >
      <p className="-mt-1 mb-4 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Freezes today's confirmed {scale === 'levels' ? 'levels' : 'scores'} and evidence as a record you can compare and export · {index.name} {index.version}</p>
      <form id="rd-review-save" onSubmit={submit}>
        <div className="grid gap-3 sm:grid-cols-[1fr_170px]">
          <RdField label="Name" htmlFor="as-name" required hint={duplicate ? <span style={{ color: 'var(--rd-status-warn)' }} data-testid="name-duplicate">An assessment with this name already exists.</span> : undefined}>
            <input id="as-name" className="rd-input" value={name} onChange={(e) => setName(e.target.value)} aria-describedby={duplicate ? 'as-name-dup' : undefined} />
          </RdField>
          <RdField label="As-of date" htmlFor="as-asof" hint={minAsOf ? `No earlier than ${formatDate(last!.created_at)}` : undefined}>
            <input id="as-asof" className="rd-input" type="date" value={asOf} min={minAsOf} max={today.toISOString().slice(0, 10)} onChange={(e) => setAsOf(e.target.value)} />
          </RdField>
        </div>
        <RdField label="Note (optional)" htmlFor="as-note">
          <input id="as-note" className="rd-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this assessment matters" />
        </RdField>

        {/* Needs attention (non-blocking) */}
        <section className="mt-2" aria-labelledby="rd-needs">
          <p id="rd-needs" className="rd-label">Needs attention ({gates.needs_attention.length})</p>
          {gates.needs_attention.length === 0 ? (
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Every set level is confirmed.</p>
          ) : (
            <ul className="mt-1.5 space-y-1">
              {gates.needs_attention.map((r) => (
                <li key={r.code} className="flex flex-wrap items-center gap-2 rounded-lg px-3 py-1.5 text-[12.5px]" style={{ background: 'var(--rd-status-warn-bg)' }}>
                  <Icon name="warn" size={13} style={{ color: 'var(--rd-status-warn)' }} />
                  <span className="rd-mono font-semibold">{r.code}</span>
                  <span className="min-w-0 flex-1">
                    Suggested {scale === 'levels' ? r.suggested_level : fmtScore(r.suggested_score)} not confirmed · will be recorded as {scale === 'levels' ? r.recorded_level : fmtScore(r.recorded_score)}
                    {r.last_confirmed_at ? ` (last confirmed ${formatDate(r.last_confirmed_at)})` : scale === 'levels' ? ' (L0)' : ' (not scored)'}
                  </span>
                  <Link to={`/readiness/${r.code}${q}&return=review`} className="rd-link" onClick={onClose}>Confirm →</Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Blocking checks */}
        <section className="mt-4" aria-labelledby="rd-blocking">
          <p id="rd-blocking" className="rd-label">Blocking checks {gates.blocking.length === 0 ? '(none today)' : `(${gates.blocking.length})`}</p>
          <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>A level at the top of the scale with no evidence, or a level changed since the last assessment with no note, blocks saving.</p>
          {gates.blocking.length > 0 && (
            <ul className="mt-1.5 space-y-1" data-testid="blocking-checks">
              {gates.blocking.map((b) => (
                <li key={`${b.code}-${b.kind}`} className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-[12.5px]" style={{ background: 'var(--rd-bg-tile)', border: '1px solid var(--rd-status-warn)' }}>
                  <Icon name="warn" size={13} style={{ color: 'var(--rd-status-warn)' }} />
                  <span className="min-w-0 flex-1">{b.text}</span>
                  <Link to={`/readiness/${b.code}${q}`} className="rd-link" onClick={onClose}>Fix →</Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Movement */}
        <section className="mt-4" aria-labelledby="rd-movement">
          <p id="rd-movement" className="rd-label">Movement since {last ? last.name : 'the start'}</p>
          <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}>
            {movement.up.length} up · {movement.down.length} down{movement.coverage.length ? ` · ${movement.coverage.length} down (coverage)` : ''} · {movement.unchanged} unchanged · {movement.notSet.length} not set
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {movement.up.map((m) => <Chip key={m.code} tone="ok">↑ {m.code} {m.from} → {m.to}</Chip>)}
            {movement.down.map((m) => <Chip key={m.code} tone="warn">▼ {m.code} {m.from} → {m.to}</Chip>)}
            {movement.coverage.map((m) => <Chip key={m.code} tone="tile" style={{ background: 'var(--rd-coverage-bg)', color: 'var(--rd-coverage)' }}>▼ {m.code} {m.from} → {m.to} · coverage-driven</Chip>)}
            {movement.notSet.map((c) => <Chip key={c} tone="dashed">{c} recorded as {scale === 'levels' ? 'L0' : 'unscored'}</Chip>)}
          </div>
        </section>

        {/* What is recorded */}
        <section className="mt-4" aria-labelledby="rd-recorded">
          <p id="rd-recorded" className="rd-label">What is recorded</p>
          <div className="mt-1.5 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <Tile label="Score recorded" value={fmt(recordedScore)} hint={`${fmt(liveScoreValue)} live incl. suggested`} testId="recorded-score" />
            <Tile label="Evidence frozen" value={String(evidenceFrozen)} hint="items" />
            <Tile label="Assets not migrated" value={summary ? summary.assets_not_migrated.toLocaleString() : '—'} hint={summary ? 'from Discovery' : 'Discovery not connected'} />
            <Tile label="Source of levels" value={basisLabel} hint="" small>
              {scale === 'levels' && onAttachReport && <button type="button" className="rd-link text-[11.5px]" onClick={onAttachReport}>Attach CSA QRI report</button>}
            </Tile>
          </div>
        </section>

        <p className="mt-4 flex items-start gap-2 rounded-lg px-3 py-2 text-[11.5px]" style={{ background: 'var(--rd-bg-tile)', color: 'var(--rd-text-muted)' }}>
          <Icon name="info" size={13} className="mt-0.5 shrink-0" />
          <span>Once saved the assessment is read-only, stamped “saved by {actor} · {formatDate(today.toISOString())}”. Live {scale === 'levels' ? 'levels' : 'scores'} keep updating; later changes go into the next assessment.</span>
        </p>
      </form>
    </RdModal>
  )
}

function Tile({ label, value, hint, small, testId, children }: { label: string; value: string; hint: string; small?: boolean; testId?: string; children?: React.ReactNode }) {
  return (
    <div className="rd-tile p-3">
      <p className="rd-label">{label}</p>
      <p className={`mt-1 font-bold leading-tight ${small ? 'text-[14px]' : 'rd-mono text-[18px]'}`} data-testid={testId}>{value}</p>
      {hint && <p className="mt-0.5 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>{hint}</p>}
      {children}
    </div>
  )
}

export type { LevelCode }
